import { AlertCircle, CheckCircle2, Info, PauseCircle, Sparkles, User } from "lucide-react";
import { Fragment, useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { conversationErrorText, friendlyRefs, ID_PAUSE_CODES, noticeText, refusalText, rowPathText, rowText, summaryText, progressLineText } from "@/components/agents/conversation/conversationTexts";
import { QuestionCard } from "@/components/agents/conversation/QuestionCard";
import { ScreenshotThumbnail } from "@/components/agents/conversation/ScreenshotThumbnail";
import { TerminalLogo, type TerminalLogoMotion } from "@/components/agents/conversation/TerminalLogo";
import type {
  ConversationItem,
  LogLine,
  NoticeCode,
  PendingMessage,
} from "@/components/agents/conversation/types";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/useAuth";
import { formatTime } from "@/lib/datetime";

// Cor da linha de informação por nível: sobre o fundo do console (`#061e3d`) todas passam
// de 4,5:1. O nível `error` não passa por aqui: vira o cartão de erro.
const INFO_COLOR: Record<Exclude<LogLine["level"], "error">, string> = {
  info: "text-[rgba(255,255,255,0.6)]",
  warn: "text-[#f2b84b]",
};

/** O passo numerado do log: "step 3: Click Create" (o número cru e o texto do passo). */
const STEP_LINE = /^step\s+(\d+)\s*:\s*(.*)$/is;

/** Avisos que fecham a conversa: viram o divisor do desenho. */
const DIVIDER_NOTICES: ReadonlySet<NoticeCode> = new Set([
  "conversation_completed",
  "conversation_interrupted",
  "conversation_failed",
]);

type NoticeTone = "info" | "pause" | "error" | "success";

const NOTICE_TONES: Record<NoticeTone, { box: string; icon: string; text: string; Icon: typeof Info }> = {
  info: {
    box: "border-[rgba(255,255,255,0.14)] bg-[rgba(255,255,255,0.05)]",
    icon: "text-[#9cc0ff]",
    text: "text-[rgba(255,255,255,0.88)]",
    Icon: Info,
  },
  pause: {
    box: "border-[rgba(255,200,61,0.35)] bg-[rgba(255,200,61,0.1)]",
    icon: "text-[var(--exed-gold)]",
    text: "text-[#ffe9a8]",
    Icon: PauseCircle,
  },
  error: {
    box: "border-[rgba(220,38,38,0.35)] bg-[rgba(220,38,38,0.12)]",
    icon: "text-[#f87171]",
    text: "text-[#fecaca]",
    Icon: AlertCircle,
  },
  success: {
    box: "border-[rgba(34,197,94,0.35)] bg-[rgba(34,197,94,0.1)]",
    icon: "text-[#4ade80]",
    text: "text-[#bbf7d0]",
    Icon: CheckCircle2,
  },
};

/** O tom do aviso: pausa e retomada em dourado, falha em vermelho, script gravado em verde. */
function noticeTone(code: NoticeCode, params: Record<string, string>): NoticeTone {
  switch (code) {
    case "session_paused_idle":
    case "target_read_only":
    case "script_deviation":
      return "pause";
    case "driver_error":
      return ID_PAUSE_CODES.has(params.code) ? "pause" : "error";
    case "turn_failed":
    case "login_failed":
      return "error";
    case "script_recorded":
      return "success";
    default:
      return "info";
  }
}

/** O aviso do sistema no console, dentro de um cartão (como o de erro do log). */
function NoticeCard({ seq, code, params }: { seq: number; code: NoticeCode; params: Record<string, string> }) {
  const { t } = useTranslation();
  const tone = NOTICE_TONES[noticeTone(code, params)];
  return (
    <div
      data-testid={`conversation-notice-${seq}`}
      data-tone={noticeTone(code, params)}
      className={`ml-10 mt-2 flex max-w-[88%] gap-2.5 self-start rounded-lg border px-3 py-2.5 ${tone.box}`}
    >
      <tone.Icon aria-hidden="true" className={`mt-0.5 h-4 w-4 shrink-0 ${tone.icon}`} />
      <p className={`min-w-0 whitespace-pre-wrap break-words font-sans text-[14px] leading-[1.5] ${tone.text}`}>
        {noticeText(t, code, params)}
      </p>
    </div>
  );
}

/** Balão de status do chat: o texto solto (falha de envio, resultado da linha, resumo) vai
 *  num cartão com ícone, no tom do aviso, como os `NoticeCard`. */
function ToneBalloon({
  tone,
  testId,
  align = "start",
  role,
  children,
  extra,
  outcome,
}: {
  tone: NoticeTone;
  testId: string;
  align?: "start" | "end" | "center";
  role?: "alert" | "status";
  children: ReactNode;
  extra?: ReactNode;
  outcome?: string;
}) {
  const style = NOTICE_TONES[tone];
  const self = align === "end" ? "self-end" : align === "center" ? "self-center" : "self-start";
  return (
    <div
      role={role}
      data-testid={testId}
      data-tone={tone}
      data-outcome={outcome}
      className={`ml-10 mt-2 flex max-w-[88%] items-start gap-2.5 rounded-lg border px-3 py-2.5 ${self} ${style.box}`}
    >
      <style.Icon aria-hidden="true" className={`mt-0.5 h-4 w-4 shrink-0 ${style.icon}`} />
      <div className="flex min-w-0 flex-col items-start gap-1.5">
        <p className={`min-w-0 whitespace-pre-wrap break-words font-sans text-[14px] leading-[1.5] ${style.text}`}>
          {children}
        </p>
        {extra}
      </div>
    </div>
  );
}

const ROW_TONE: Record<string, NoticeTone> = {
  created: "success",
  already_existed: "info",
  failed: "error",
  skipped: "info",
};

/** Iniciais da pessoa logada para o avatar do consultor; sem nome, o ícone. */
function initialsOf(name: string | undefined): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
}

/** "Agent · 12:34": o rótulo do balão com a hora do evento; sem data válida, só o rótulo. */
function labelWithTime(label: string, createdAt: string | undefined): string {
  if (!createdAt || Number.isNaN(new Date(createdAt).getTime())) return label;
  return `${label} · ${formatTime(createdAt)}`;
}

function LogLineItem({ line }: { line: LogLine }) {
  const { t } = useTranslation();
  if (line.level === "error") {
    return (
      <div
        data-testid="chat-error"
        data-level="error"
        className="ml-10 mt-1 flex gap-2.5 rounded-lg border border-[rgba(220,38,38,0.35)] bg-[rgba(220,38,38,0.12)] px-3 py-2.5"
      >
        <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-[#f87171]" />
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-semibold uppercase leading-normal tracking-[0.1em] text-[#f87171]">
            {t("Error")}
          </p>
          <p className="whitespace-pre-wrap break-words font-sans text-[14px] leading-[1.5] text-[#fecaca]">
            {line.message}
          </p>
        </div>
      </div>
    );
  }
  const step = STEP_LINE.exec(line.message);
  if (step) {
    return (
      <div
        data-testid="chat-step"
        data-level={line.level}
        data-step-number={step[1]}
        className="mt-1 flex max-w-[88%] gap-3 self-start rounded-lg border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.03)] px-3 py-2.5"
      >
        <span className="flex h-[22px] w-7 shrink-0 items-center justify-center rounded bg-[rgba(255,200,61,0.12)] font-mono text-[11px] font-semibold text-[var(--exed-gold)]">
          {step[1].padStart(2, "0")}
        </span>
        <span className="min-w-0 whitespace-pre-wrap break-words font-sans text-[14px] leading-[1.55] text-[rgba(255,255,255,0.92)] [text-wrap:pretty]">
          {step[2]}
        </span>
      </div>
    );
  }
  return (
    <div
      data-testid="chat-info"
      data-level={line.level}
      className={`flex gap-2 whitespace-pre-wrap break-words py-0.5 pl-[52px] pr-3 font-mono text-[12px] leading-[1.6] ${INFO_COLOR[line.level]}`}
    >
      <span aria-hidden="true" className="select-none text-[rgba(255,255,255,0.3)]">
        {"› "}
      </span>
      <span className="min-w-0">{progressLineText(t, line.message)}</span>
    </div>
  );
}

const LABEL_CLASS = "font-sans text-[11px] font-medium leading-normal text-[rgba(255,255,255,0.5)]";

/** `**trecho**` (sem espaço junto aos asteriscos) vira negrito; o resto fica como texto. */
const BOLD = /\*\*(\S(?:[^*]*\S)?)\*\*/g;

function withBold(text: string): ReactNode[] {
  // O `split` com grupo de captura alterna: texto, negrito, texto, negrito…
  return text.split(BOLD).map((part, index) =>
    index % 2 === 1 ? <strong key={index}>{part}</strong> : <Fragment key={index}>{part}</Fragment>,
  );
}

function AgentBubble({
  seq,
  text,
  createdAt,
}: {
  seq: number;
  text: string;
  createdAt?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={`conversation-agent-${seq}`}
      className="mt-3 flex max-w-[82%] items-end gap-2 self-start"
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[rgba(255,200,61,0.3)] bg-[rgba(255,200,61,0.12)] text-[var(--exed-gold)]"
      >
        <Sparkles className="h-3.5 w-3.5" />
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className={LABEL_CLASS}>{labelWithTime(t("Agent"), createdAt)}</span>
        <p
          data-testid="chat-agent-message"
          className="min-w-0 whitespace-pre-wrap break-words rounded-[12px_12px_12px_4px] border border-[rgba(255,255,255,0.08)] bg-[#12396b] px-3.5 py-2.5 font-sans text-[14px] leading-[1.55] text-white [text-wrap:pretty]"
        >
          {withBold(friendlyRefs(t, text))}
        </p>
      </div>
    </div>
  );
}

function ConsultantBubble({
  testId,
  text,
  createdAt,
  children,
}: {
  testId: string;
  text: string;
  /** Mensagem ainda não gravada (pendente) não tem hora. */
  createdAt?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const initials = initialsOf(useCurrentUser()?.name);
  return (
    <div data-testid={testId} className="mt-3.5 flex max-w-[82%] items-end gap-2 self-end">
      <div className="flex min-w-0 flex-col items-end gap-1">
        <span className={LABEL_CLASS}>{labelWithTime(t("You"), createdAt)}</span>
        <p
          data-testid="chat-consultant-message"
          className="min-w-0 whitespace-pre-wrap break-words rounded-[12px_12px_4px_12px] bg-[var(--exed-gold)] px-3.5 py-2.5 font-sans text-[14px] font-medium leading-[1.5] text-[var(--exed-navy)] shadow-[0_1px_2px_rgba(0,0,0,0.2)]"
        >
          {text}
        </p>
        {children && (
          <span className="flex items-center justify-end gap-2 font-sans text-[11px] text-[rgba(255,255,255,0.5)]">
            {children}
          </span>
        )}
      </div>
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--exed-gold)] font-sans text-[11px] font-bold text-[var(--exed-navy)] shadow-[0_0_0_2px_#061e3d,0_0_0_3px_rgba(255,200,61,0.4)]"
      >
        {initials || <User className="h-3.5 w-3.5" />}
      </span>
    </div>
  );
}

/**
 * A linha do tempo da conversa: balões, cartões, prints, linhas do recorte, avisos
 * e o log técnico.
 *
 * Tudo no mesmo fundo escuro, no desenho do Claude Design: as linhas `step N:` do log
 * viram passos numerados, o resto do log é linha de informação, o erro tem cartão
 * próprio, a fala do agente é balão azul à esquerda e a do consultor, balão amarelo à
 * direita. A lista acompanha o fim: a pessoa conversa em tempo real.
 * As mensagens que ainda não viraram evento aparecem por último, com o estado, e o
 * logo do GROW fecha o log (`motion` diz o que ele faz). Sem itens, `emptyHint` ocupa
 * o lugar do log (o terminal sem execução selecionada), com o cursor no lugar do logo.
 */
/** O agente já mandou mensagem depois da última fala do consultor, antes do item. */
function agentSpokeSinceConsultant(items: ConversationItem[], index: number): boolean {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const kind = items[cursor].kind;
    if (kind === "agent_message") return true;
    if (kind === "consultant_message") return false;
  }
  return false;
}

export function ConversationMessageList({
  projectId,
  runId,
  items,
  pending,
  onRetry,
  disabled = false,
  watching = false,
  motion = "still",
  emptyHint,
}: {
  projectId: string;
  runId: string;
  items: ConversationItem[];
  pending: PendingMessage[];
  disabled?: boolean;
  /** Quem vê só acompanha (não é o executor): repassado aos cartões de pergunta. */
  watching?: boolean;
  onRetry: (clientMessageId: string) => void;
  /** O que o logo do fim do log faz agora. */
  motion?: TerminalLogoMotion;
  /** Dica no lugar do log quando não há item nenhum: a linha "> …" com o cursor piscando. */
  emptyHint?: string;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const empty = items.length === 0 && pending.length === 0 && !!emptyHint;
  // Logs consecutivos crescem no mesmo bloco, sem aumentar a quantidade de itens.
  const logLineCount = items.reduce(
    (total, item) => total + (item.kind === "log" ? item.lines.length : 0),
    0,
  );

  // Posição de cada print entre os prints da conversa, para o rótulo "Print 2 de 5".
  const screenshots = items.filter((item) => item.kind === "screenshot");

  // O último item do log: um bloco de log cresce por linha, então vale a última linha.
  const lastItem = items[items.length - 1];
  const lastLine = lastItem?.kind === "log" ? lastItem.lines[lastItem.lines.length - 1] : null;
  const lastSeq = lastLine ? lastLine.seq : (lastItem?.seq ?? 0);

  useEffect(() => {
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [items.length, pending.length, logLineCount]);

  return (
    <div
      ref={scrollRef}
      role="log"
      data-testid="conversation-messages"
      className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-4 font-sans text-[13px] leading-[1.6] text-[#d6dde8] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#2a5a9a] [&::-webkit-scrollbar-track]:bg-[#061e3d] [&::-webkit-scrollbar]:w-2"
    >
      {empty && (
        <div className="flex gap-2 font-mono text-[rgba(255,255,255,0.75)]">
          <span aria-hidden="true" className="select-none text-[var(--exed-gold)]">
            {"> "}
          </span>
          <span className="min-w-0">{emptyHint}</span>
          <span
            aria-hidden="true"
            data-testid="terminal-cursor"
            className="mt-[3px] h-4 w-2 shrink-0 bg-[var(--exed-gold)] motion-safe:animate-ex-blink"
          />
        </div>
      )}
      {items.map((item, index) => {
        switch (item.kind) {
          case "agent_message":
            return (
              <AgentBubble
                key={item.seq}
                seq={item.seq}
                text={item.text}
                createdAt={item.createdAt}
              />
            );
          case "refusal":
            return <AgentBubble key={item.seq} seq={item.seq} text={refusalText(t, item.code)} />;
          case "consultant_message": {
            const unread = pending.some((message) => message.clientMessageId === item.clientMessageId);
            return (
              <ConsultantBubble
                key={item.seq}
                testId={`conversation-consultant-${item.seq}`}
                text={item.text}
                createdAt={item.createdAt}
              >
                {unread && <span>{t("Queued")}</span>}
              </ConsultantBubble>
            );
          }
          case "question":
            return (
              <QuestionCard
                key={item.seq}
                item={item}
                disabled={disabled}
                watching={watching}
                pendingAnswer={pending.some((message) => message.replyTo === item.questionId && message.state !== "failed")}
              />
            );
          case "approval":
            // Não há cartão nem botão: o pedido é uma mensagem do agente, e se aprova ou
            // recusa escrevendo. Se o agente já falou nesta rodada, a mensagem dele é o
            // pedido e este não repete.
            return agentSpokeSinceConsultant(items, index) ? null : (
              <AgentBubble key={item.seq} seq={item.seq} text={item.summary} />
            );
          case "screenshot":
            return (
              <ScreenshotThumbnail
                key={item.seq}
                projectId={projectId}
                runId={item.runId ?? runId}
                item={item}
                position={{ index: screenshots.indexOf(item) + 1, total: screenshots.length }}
              />
            );
          case "notice":
            if (DIVIDER_NOTICES.has(item.code)) {
              return (
                <div
                  key={item.seq}
                  data-testid={`conversation-notice-${item.seq}`}
                  data-divider="true"
                  className="mt-3.5 flex items-center gap-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-[rgba(255,255,255,0.4)]"
                >
                  <span aria-hidden="true" className="h-px flex-1 bg-[rgba(255,255,255,0.1)]" />
                  <span>{noticeText(t, item.code, item.params)}</span>
                  <span aria-hidden="true" className="h-px flex-1 bg-[rgba(255,255,255,0.1)]" />
                </div>
              );
            }
            return <NoticeCard key={item.seq} seq={item.seq} code={item.code} params={item.params} />;
          case "row":
            return (
              <ToneBalloon
                key={item.seq}
                tone={ROW_TONE[item.outcome] ?? "info"}
                testId={`conversation-row-${item.seq}`}
                outcome={item.outcome}
              >
                {`${rowText(t, item.outcome, item.rowIndex)} · ${rowPathText(t, item.path)}`}
                {item.sapMessage ? ` — ${item.sapMessage}` : ""}
              </ToneBalloon>
            );
          case "summary":
            // Mesmo `seq` do aviso do `conversation_ended`: a chave leva o tipo.
            return (
              <ToneBalloon
                key={`summary-${item.seq}`}
                tone="info"
                align="center"
                testId={`conversation-summary-${item.seq}`}
              >
                {summaryText(t, item)}
              </ToneBalloon>
            );
          case "log":
            return (
              <div
                key={item.seq}
                role="group"
                aria-label={t("Technical log")}
                data-testid={`conversation-log-${item.seq}`}
                className="flex flex-col gap-1.5"
              >
                {item.lines.map((line) => (
                  <LogLineItem key={line.seq} line={line} />
                ))}
              </div>
            );
          default:
            return null;
        }
      })}
      {pending.filter((message) => !items.some((item) =>
        item.kind === "consultant_message" && item.clientMessageId === message.clientMessageId,
      )).map((message) => (
        <Fragment key={message.clientMessageId}>
          <ConsultantBubble
            testId={`conversation-pending-${message.clientMessageId}`}
            text={message.text}
          >
            <span>
              {message.state === "failed"
                ? t("Not sent")
                : message.state === "queued"
                  ? t("Queued")
                  : t("Sending...")}
            </span>
          </ConsultantBubble>
          {message.state === "failed" && (
            <ToneBalloon
              tone="error"
              align="end"
              role="alert"
              testId={`conversation-pending-error-${message.clientMessageId}`}
              extra={message.retryable && !disabled ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 border-[rgba(255,255,255,0.25)] bg-transparent px-3 text-[12px] text-[#9cc0ff] hover:bg-[rgba(255,255,255,0.08)]"
                  onClick={() => onRetry(message.clientMessageId)}
                >
                  {t("Retry")}
                </Button>
              ) : undefined}
            >
              {conversationErrorText(t, message.errorCode)}
            </ToneBalloon>
          )}
        </Fragment>
      ))}
      {/* Sem execução o cursor da linha "> …" ocupa o lugar do logo. */}
      {!empty && (
        <div className="flex items-center gap-2 px-1 pt-2 font-sans text-[13px] text-[rgba(255,255,255,0.55)]">
          <TerminalLogo motion={motion} pulseSeq={lastSeq} />
          {/* O cabeçalho já anuncia o estado: o texto é só visual, sem repetir na região viva. */}
          {motion !== "still" && (
            <span aria-hidden="true" data-testid="chat-typing">
              {motion === "working" ? t("Agent is working…") : t("Waiting for your answer")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
